// src/components/CandidateHiring.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
// import "./CandidateHiring.css";
import API_BASE_URL from "../config";

const CandidateHiring = () => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [allCandidates, setAllCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleUpload = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!file) {
      alert("Please upload a resume PDF!");
      setIsLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("resume", file);
      formData.append("name", name);
      formData.append("email", email);

      const res = await axios.post(`${API_BASE_URL}/api/candidates/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setResult(res.data.candidate);
      fetchCandidates();
    } catch (err) {
      console.error("❌ Upload Error:", err.response?.data || err.message);
      alert("Upload failed: " + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/candidates/all`);
      setAllCandidates(res.data);
    } catch (err) {
      console.error("❌ Fetch Error:", err);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, []);

  return (
    <div className="candidateHiringContainer">
      {/* Animated background SVG */}
      <div className="animatedBackground">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="backgroundSvg">
          <path fill="#f0f7ff" d="M0,0 L100,0 L100,100 L0,100 Z" />
          <path fill="#e1efff" d="M0,0 C50,50 50,50 100,0 L100,100 L0,100 Z" />
          <circle cx="20" cy="30" r="5" fill="#4d79ff" opacity="0.2" />
          <circle cx="80" cy="70" r="8" fill="#4d79ff" opacity="0.2" />
          <circle cx="40" cy="80" r="6" fill="#ff6b6b" opacity="0.2" />
          <circle cx="70" cy="20" r="4" fill="#ff6b6b" opacity="0.2" />
        </svg>
      </div>

      <div className="candidateHiringContent">
        <header className="candidateHeader">
          <h1 className="gradientTitle">AI-Powered Candidate Hiring</h1>
          <p className="headerSubtitle">
            Find the perfect talent match with our advanced AI resume analysis system
          </p>
        </header>

        <div className="uploadSection">
          <div className="uploadInfo">
            <h2>Upload Candidate Resume</h2>
            <p>
              Our AI system analyzes resumes to match candidates with ideal roles based on skills, 
              experience, and qualifications. Upload PDF resumes to get started.
            </p>
            <div className="featuresGrid">
              <div className="featureItem">
                <div className="featureIcon">🤖</div>
                <h3>AI Analysis</h3>
                <p>Advanced parsing and skill extraction</p>
              </div>
              <div className="featureItem">
                <div className="featureIcon">🎯</div>
                <h3>Role Matching</h3>
                <p>Intelligent role compatibility scoring</p>
              </div>
              <div className="featureItem">
                <div className="featureIcon">📊</div>
                <h3>Detailed Insights</h3>
                <p>Comprehensive candidate evaluation</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleUpload} className="uploadForm">
            <div className="formGroup">
              <label htmlFor="name">Candidate Name</label>
              <input
                type="text"
                id="name"
                placeholder="Enter full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            
            <div className="formGroup">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="formGroup">
              <label htmlFor="resume" className="fileInputLabel">
                <span>Upload Resume (PDF)</span>
                <div className="fileInputButton">Choose File</div>
                {file && <span className="fileName">{file.name}</span>}
              </label>
              <input
                type="file"
                id="resume"
                accept=".pdf"
                onChange={(e) => setFile(e.target.files[0])}
                required
                style={{ display: 'none' }}
              />
            </div>
            
            <button type="submit" className="submitButton" disabled={isLoading}>
              {isLoading ? 'Analyzing Resume...' : 'Upload & Analyze'}
              {isLoading && <div className="loadingSpinner"></div>}
            </button>
          </form>
        </div>

        {result && (
          <div className="resultSection">
            <h2>Analysis Results</h2>
            <div className="resultCard">
              <div className="resultHeader">
                <h3 className="gradientText">{result.bestRole}</h3>
                <div className="scoreBadge">
                  Match Score: <span>{result.matchScore}%</span>
                </div>
              </div>
              <div className="skillsSection">
                <h4>Skills Extracted:</h4>
                <div className="skillsList">
                  {result.skills.map((skill, index) => (
                    <span key={index} className="skillTag">{skill}</span>
                  ))}
                </div>
              </div>
              <div className="candidateInfo">
                <p><strong>Name:</strong> {result.name}</p>
                <p><strong>Email:</strong> {result.email}</p>
              </div>
            </div>
          </div>
        )}

        <div className="candidatesTableSection">
          <div className="sectionHeader">
            <h2>All Candidates</h2>
            <p>Review previously analyzed candidates and their match scores</p>
          </div>
          
          <div className="tableContainer">
            <table className="candidatesTable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Score</th>
                  <th>Skills</th>
                </tr>
              </thead>
              <tbody>
                {allCandidates.map(c => (
                  <tr key={c._id}>
                    <td className="candidateName">{c.name}</td>
                    <td>{c.email}</td>
                    <td>
                      <span className="roleBadge">{c.bestRole}</span>
                    </td>
                    <td>
                      <div className="scoreMeter">
                        <div 
                          className="scoreFill" 
                          style={{width: `${c.matchScore}%`}}
                        ></div>
                        <span>{c.matchScore}%</span>
                      </div>
                    </td>
                    <td>
                      <div className="tableSkills">
                        {c.skills.slice(0, 3).map((skill, index) => (
                          <span key={index} className="skillTag small">{skill}</span>
                        ))}
                        {c.skills.length > 3 && (
                          <span className="skillMore">+{c.skills.length - 3} more</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateHiring;